/**
 * Geospatial Service
 * Provides geospatial lookup functions for determining district boundaries
 */
import { supabase } from '@/integrations/supabase/client';

// Common error responses
const ERRORS = {
  NO_COORDINATES: "No coordinates provided",
  API_ERROR: "Error accessing geospatial service",
  OUTSIDE_DISTRICT: "Location is outside known district boundaries"
};

// Types for confidence levels in our validation
export type ConfidenceLevel = "high" | "medium" | "low";

// Structure for district determination results
export interface DistrictDetermination {
  district: string | null;
  confidenceLevel: ConfidenceLevel;
  method: string;
  metadata?: Record<string, any>;
}

/**
 * Uses PostGIS to determine district and grade-specific boundary school
 */
export async function determineDistrictByCoordinatesWithGrade(
  lat: number,
  lng: number,
  grade: string = 'K'
): Promise<DistrictDetermination> {
  try {
    if (!lat || !lng) {
      return {
        district: null,
        confidenceLevel: "low",
        method: "coordinates_missing"
      };
    }

    // Call grade-specific Supabase RPC
    const { data, error } = await supabase.rpc('find_school_district_by_grade', { lat, lon: lng, grade });
    
    if (error) throw error;
    if (!data || data.length === 0 || !data[0].district_name) {
      return {
        district: null,
        confidenceLevel: "high",
        method: "geospatial_boundary_grade",
        metadata: { outOfBounds: true, coordinates: { lat, lng }, grade }
      };
    }
    
    const result = data[0];
    
    // Format boundary school name with school level
    const formatBoundarySchoolName = (schoolName: string, schoolLevel: string): string => {
      if (!schoolName) return schoolName;
      
      const levelSuffixes = {
        'elementary': 'Elementary School',
        'middle': 'Middle School', 
        'high': 'High School'
      };
      
      const suffix = levelSuffixes[schoolLevel as keyof typeof levelSuffixes];
      if (suffix && !schoolName.toLowerCase().includes('school')) {
        return `${schoolName} ${suffix}`;
      }
      
      return schoolName;
    };
    
    const formattedBoundarySchool = formatBoundarySchoolName(result.boundary_school, result.school_level);
    
    return {
      district: result.district_name,
      confidenceLevel: "high",
      method: "geospatial_boundary_grade",
      metadata: { 
        coordinates: { lat, lng },
        grade,
        schoolLevel: result.school_level,
        boundarySchool: formattedBoundarySchool,
        originalSchoolName: result.boundary_school, // Keep original name for reference
        districtId: result.district_id,
        allSchools: {
          elementary: result.elem_name,
          middle: result.midd_name,
          high: result.high_name
        }
      }
    };
  } catch (error) {
    return {
      district: null, 
      confidenceLevel: "low",
      method: "geospatial_error_grade",
      metadata: { error: error instanceof Error ? error.message : "Unknown error", grade }
    };
  }
}

/**
 * Uses PostGIS to accurately determine which district a point falls within
 */
export async function determineDistrictByCoordinates(
  lat: number, 
  lng: number
): Promise<DistrictDetermination> {
  try {
    if (!lat || !lng) {
      return {
        district: null,
        confidenceLevel: "low",
        method: "coordinates_missing"
      };
    }

    // Call Supabase RPC for geospatial lookup
    const { data, error } = await supabase.rpc('find_school_district', { lat, lon: lng });
    
    if (error) throw error;
    if (!data || data.length === 0 || !data[0].district_name) {
      return {
        district: null,
        confidenceLevel: "high",
        method: "geospatial_boundary",
        metadata: { outOfBounds: true, coordinates: { lat, lng } }
      };
    }
    
    // Format boundary school name with school level (for elementary schools)
    const formatBoundarySchoolName = (schoolName: string): string => {
      if (!schoolName) return schoolName;
      
      if (!schoolName.toLowerCase().includes('school')) {
        return `${schoolName} Elementary School`;
      }
      
      return schoolName;
    };
    
    const formattedBoundarySchool = formatBoundarySchoolName(data[0].boundary_school || data[0].elem_name);
    
    return {
      district: data[0].district_name,
      confidenceLevel: "high",
      method: "geospatial_boundary",
      metadata: { 
        coordinates: { lat, lng },
        boundarySchool: formattedBoundarySchool,
        originalSchoolName: data[0].boundary_school || data[0].elem_name,
        schoolLevel: 'elementary', // Standard lookup defaults to elementary
        districtId: data[0].district_id
      }
    };
  } catch (error) {
    return {
      district: null, 
      confidenceLevel: "low",
      method: "geospatial_error",
      metadata: { error: error instanceof Error ? error.message : "Unknown error" }
    };
  }
}

/**
 * Uses a combination of geospatial data and city name to determine district
 * More accurate than either method alone
 */
export async function determineDistrictByCoordinatesAndCity(
  lat: number,
  lng: number,
  city: string
): Promise<DistrictDetermination> {
  try {
    // First try direct geospatial lookup
    const geospatialResult = await determineDistrictByCoordinates(lat, lng);
    
    if (geospatialResult.district) {
      return geospatialResult;
    }
    
    // If no result from geospatial, call our database function that includes city
    const { data, error } = await supabase.rpc('determine_school_district', {
      lat,
      lng,
      city_name: city
    });
    
    if (error) {
      throw error;
    }
    
    if (!data) {
      return {
        district: null,
        confidenceLevel: "medium", 
        method: "combined_no_match",
        metadata: { city }
      };
    }
    
    // Verify result is an NWSISD member district
    const isNwsisdMember = []
      .some(
        district => data.includes(district) || district.includes(data)
      );
    
    return {
      district: data,
      confidenceLevel: isNwsisdMember ? "high" : "medium",
      method: "combined_geospatial_city",
      metadata: { 
        isNwsisdMember,
        city, 
        coordinates: { lat, lng }
      }
    };
    
  } catch (error) {
    
    return {
      district: null,
      confidenceLevel: "low",
      method: "combined_error",
      metadata: { error: error instanceof Error ? error.message : "Unknown error" }
    };
  }
}

/**
 * Admin-only function to determine ANY district and grade-specific boundary school (not just NWSISD)
 * This is a simplified version that just uses the existing regular function
 */
export async function determineAnyDistrictByCoordinatesWithGrade(
  lat: number,
  lng: number,
  grade: string = 'K'
): Promise<DistrictDetermination> {
  // Just use the existing regular function - it's proven and accurate
  const regularResult = await determineDistrictByCoordinatesWithGrade(lat, lng, grade);
  
  if (regularResult && regularResult.district) {
    // Convert the regular result to admin format with isNwsisdMember info
    return {
      ...regularResult,
      method: "admin_using_regular_function",
      metadata: {
        ...regularResult.metadata,
        isNwsisdMember: true, // Regular function only returns NWSISD districts
        note: "Using proven regular validation function"
      }
    };
  }
  
  // If regular function finds nothing, it means address is outside all boundaries or not in NWSISD
  console.log('ℹ️ Regular function found no district - address likely outside NWSISD boundaries');
  
  return {
    district: "Outside NWSISD boundaries",
    confidenceLevel: "high",
    method: "admin_outside_nwsisd",
    metadata: {
      coordinates: { lat, lng },
      grade,
      schoolLevel: ['6', '7', '8'].includes(grade) ? 'middle' : (['9', '10', '11', '12'].includes(grade) ? 'high' : 'elementary'),
      boundarySchool: "Address is outside NWSISD member district boundaries",
      isNwsisdMember: false,
      message: "This address is not within any NWSISD member district. For district information outside NWSISD, use the regular application validation."
    }
  };
}

/**
 * Calls the backend endpoint to get the authoritative district from the state GIS site
 */
export async function getOfficialDistrictFromState(address: string): Promise<string | null> {
  try {
    const response = await fetch('/api/geospatial/official-district-lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address })
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.district || null;
  } catch (error) {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Widened district resolution
//
// determineDistrictByCoordinates() above calls find_school_district(), which
// only ever returns one of NWSISD's seven member districts. For any address
// outside them it returns no rows, which that function reports as
// `{ district: null, metadata: { outOfBounds: true } }`. Callers cannot tell
// that apart from a failed geocode, so a family moving out of the service area
// looks identical to "we could not work it out" — and the caller keeps
// whatever stale district the record already held.
//
// resolveDistrictForPoint() calls resolve_district_for_point() instead, which
// covers every Minnesota district and reports membership explicitly. Use it
// wherever "they moved out of the area" is a meaningful answer rather than an
// error. The older function is left in place: it is load-bearing for the
// in-district validation flow, and its empty-result contract is relied on
// elsewhere.
// ---------------------------------------------------------------------------

/**
 * NWSISD member districts, keyed by the state district number MDE reports.
 * Zero-padded, exactly as `mde_attendance_areas.sdnumber` stores them.
 *
 * Keyed on number rather than name deliberately: the district *name* has ~34
 * spellings in production data and at least five competing vocabularies in
 * this codebase, so any name-based membership test is a coin flip. The number
 * is stable and is what the resolver returns.
 */
export const NWSISD_MEMBER_DISTRICT_NUMBERS: ReadonlySet<string> = new Set([
  '0011', // Anoka-Hennepin
  '0014', // Fridley
  '0279', // Osseo
  '0286', // Brooklyn Center
  '0728', // Elk River
  '0877', // Buffalo-Hanover-Montrose
  '0883', // Rockford
]);

/** Normalises a district number to the zero-padded 4-char form MDE uses. */
export function normalizeDistrictNumber(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const digits = String(value).trim();
  if (!/^\d{1,4}$/.test(digits)) return null;
  return digits.padStart(4, '0');
}

/** True only for the seven NWSISD member districts. Unknown input is not a member. */
export function isNwsisdMemberDistrictNumber(value: string | number | null | undefined): boolean {
  const n = normalizeDistrictNumber(value);
  return n !== null && NWSISD_MEMBER_DISTRICT_NUMBERS.has(n);
}

export type DistrictMatchConfidence = 'exact' | 'ambiguous' | 'none';

export interface ResolvedDistrict {
  /** Zero-padded state district number, or null when nothing contained the point. */
  districtNumber: string | null;
  /** MDE's own name for the district. NOT canonicalised to any app vocabulary. */
  districtName: string | null;
  /** True for the seven NWSISD members. Null when nothing resolved. */
  isNwsisdMember: boolean | null;
  /** Attendance-area school for the grade supplied, null when no grade was given. */
  boundarySchool: string | null;
  boundaryElem: string | null;
  boundaryMidd: string | null;
  boundaryHigh: string | null;
  matchConfidence: DistrictMatchConfidence;
  /**
   * True when a district polygon contained the point. False means we do not
   * know where this address is — an out-of-state address, a gap in MDE
   * coverage, or a bad geocode. Callers must NOT render `resolved: false`
   * as "outside NWSISD"; that is `resolved: true, isNwsisdMember: false`.
   */
  resolved: boolean;
  error?: string;
}

const UNRESOLVED: ResolvedDistrict = {
  districtNumber: null,
  districtName: null,
  isNwsisdMember: null,
  boundarySchool: null,
  boundaryElem: null,
  boundaryMidd: null,
  boundaryHigh: null,
  matchConfidence: 'none',
  resolved: false,
};

/**
 * Resolves the school district containing a point, across all of Minnesota.
 *
 * @param grade Optional. Selects the attendance-area school for the right
 *              level. Omit it and `boundarySchool` comes back null rather than
 *              defaulting to the elementary school — a default that would
 *              hand an elementary boundary school to a high schooler.
 */
export async function resolveDistrictForPoint(
  lat: number,
  lng: number,
  grade?: string | null
): Promise<ResolvedDistrict> {
  if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ...UNRESOLVED, error: ERRORS.NO_COORDINATES };
  }

  try {
    const { data, error } = await supabase.rpc('resolve_district_for_point', {
      p_lat: lat,
      p_lon: lng,
      p_grade: grade ?? null,
    });

    if (error) throw error;

    const row = Array.isArray(data) ? data[0] : data;
    if (!row || !row.district_number) {
      return { ...UNRESOLVED, matchConfidence: 'none' };
    }

    const districtNumber = normalizeDistrictNumber(row.district_number);

    return {
      districtNumber,
      districtName: row.district_name_mde ?? null,
      // Trust the column the resolver returns; fall back to the local set only
      // if the column is somehow absent, so the two can never silently diverge.
      isNwsisdMember:
        typeof row.is_nwsisd_member === 'boolean'
          ? row.is_nwsisd_member
          : isNwsisdMemberDistrictNumber(districtNumber),
      boundarySchool: row.boundary_school ?? null,
      boundaryElem: row.boundary_elem ?? null,
      boundaryMidd: row.boundary_midd ?? null,
      boundaryHigh: row.boundary_high ?? null,
      matchConfidence: (row.match_confidence as DistrictMatchConfidence) ?? 'exact',
      resolved: true,
    };
  } catch (error) {
    return {
      ...UNRESOLVED,
      error: error instanceof Error ? error.message : ERRORS.API_ERROR,
    };
  }
}
